import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, IconButton } from '@material-ui/core';
import {
  LocalHospitalRounded as SickIcon,
  CancelRounded as CancelIcon,
  EditRounded as EditIcon,
  AddRounded as AddIcon
} from '@material-ui/icons';
import moment from 'moment';
import { addWeekDays, isWeekendDay } from 'moment-business';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import {
  isOrgReadOnly,
  localizeLeaveType,
  publicHolidayDateForYear,
  useSpot
} from 'framework';
import { LeaveRequest } from 'domain-types';
import {
  Row,
  Spacer,
  Table,
  Line,
  ConfirmDialog,
  RequestLeaveDialog,
  LeaveRequestResult,
  LimitedText,
  getLeaveIcon,
  getLeaveTypeIcon
} from 'components';

interface LeaveRequestDialogDesc {
  callback: (leaveRequest: LeaveRequestResult) => Promise<void>;
  initialValue: LeaveRequestResult;
  title: string;
  text: string;
}

interface ConfirmDialogDesc {
  callback: () => Promise<void>;
  title: string;
  text: string;
}

export function LeaveRequests({
  id,
  organization
}: {
  id: string;
  organization: string;
}) {
  const { t } = useTranslation();
  const { data, command, query } = useSpot();

  const user = data?.users?.[id];
  const org = data?.organizations?.[organization];
  const leaveRequests = data?.leaveRequests?.[id] ?? [];
  const readOnly = isOrgReadOnly(org);

  const [
    requestLeaveDialog,
    setRequestLeaveDialog
  ] = useState<LeaveRequestDialogDesc | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogDesc | null>(
    null
  );

  const refreshLeaveRequests = useCallback(async () => {
    await query(`org/${organization}/user/${id}/leave`, {}, [
      'leaveRequests',
      id
    ]);
  }, [organization, id, query]);

  const shouldLookupUser = !data?.users?.[id];

  useEffect(() => {
    (async () => {
      if (shouldLookupUser) {
        await query(`org/${organization}/user/${id}`, {}, ['users', id]);
      }
      await refreshLeaveRequests();
    })();
  }, [refreshLeaveRequests, query, organization, id, shouldLookupUser]);

  const canCancelLeave = (leave: LeaveRequest) => {
    const tooOld = moment(leave.startDate, 'YYYY-MM-DD').isBefore(
      moment(),
      'day'
    );
    const wrongState =
      leave.state !== 'requested' &&
      leave.state !== 'filed' &&
      leave.state !== 'approved';
    return !tooOld && !wrongState;
  };

  const getResetHours = (startDate: string, endDate: string) => {
    const hasPublicHolidayOnDate = (currentDate: string) =>
      (org?.publicHolidays ?? []).some(
        holiday =>
          publicHolidayDateForYear(
            holiday.date,
            moment(currentDate, 'YYYY-MM-DD')
          ).format('YYYY-MM-DD') === currentDate
      );
    const newHours: Record<string, number> = {};
    let current = moment(startDate);
    if (isWeekendDay(current)) {
      current = addWeekDays(current, 1);
    }
    const endDatePlusOne = moment(endDate).add(1, 'day');

    while (current.isBefore(endDatePlusOne)) {
      const currentDate = current.format('YYYY-MM-DD');
      const isPublicHoliday = hasPublicHolidayOnDate(currentDate);

      newHours[currentDate] = isPublicHoliday
        ? 0
        : user?.hoursPerDay ?? org?.hoursPerDay ?? 0;
      current = addWeekDays(current, 1);
    }

    return newHours;
  };

  const onCancelLeaveRequest = (requestId: string) => {
    if (readOnly) {
      return;
    }

    setConfirmDialog({
      callback: async () => {
        await command(
          `org/${organization}/user/${id}/leave/${requestId}/cancel`
        );
        await refreshLeaveRequests();

        setConfirmDialog(null);
      },
      title: t('confirmCancelLeaveRequestTitle'),
      text: t('confirmCancelLeaveRequestSubtitle')
    });
  };

  const onRequestLeave = () => {
    if (readOnly) {
      return;
    }

    setRequestLeaveDialog({
      callback: async (leaveRequest: LeaveRequestResult) => {
        await command(`org/${organization}/user/${id}/leave`, {
          ...leaveRequest
        });
        await refreshLeaveRequests();

        setRequestLeaveDialog(null);
      },
      initialValue: { type: leaveTypes[0] },
      title: t('requestLeaveTitle'),
      text: t('requestLeaveSubtitle')
    });
  };

  const onEditLeaveRequest = (leave: LeaveRequest) => {
    if (readOnly) {
      return;
    }

    setRequestLeaveDialog({
      callback: async (leaveRequest: LeaveRequestResult) => {
        await command(
          `org/${organization}/user/${id}/leave/${leave.id}/update`,
          {
            ...leaveRequest
          }
        );
        await refreshLeaveRequests();
        setRequestLeaveDialog(null);
      },
      initialValue: {
        startDate: leave.startDate,
        endDate:
          leave.endDate ??
          (leave.type === 'sick' ? undefined : leave.startDate),
        reason: leave.reason,
        type: leave.type,
        hours:
          leave.hours ??
          getResetHours(leave.startDate, leave.endDate ?? leave.startDate)
      },
      title:
        leave.type === 'sick'
          ? t('editSickLeaveTitle')
          : t('requestLeaveTitle'),
      text:
        leave.type === 'sick' ? t('sickPrivacyHint') : t('requestLeaveSubtitle')
    });
  };

  const leaveTypes = Object.keys(org?.leaveTypes || {}).filter(
    type => !(org?.disabledLeaveTypes ?? []).includes(type)
  );
  const canUseSickLeave = leaveTypes.includes('sick');
  const activeSickLeave = [...leaveRequests]
    .sort((reqA, reqB) => reqB.startDate.localeCompare(reqA.startDate))
    .find(
      leave =>
        leave.type === 'sick' &&
        !leave.endDate &&
        leave.state !== 'canceled' &&
        leave.state !== 'rejected'
    );

  const getAmsterdamDate = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Amsterdam',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());
    const day = parts.find(part => part.type === 'day')?.value ?? '01';
    const month = parts.find(part => part.type === 'month')?.value ?? '01';
    const year = parts.find(part => part.type === 'year')?.value ?? '1970';
    return `${year}-${month}-${day}`;
  };

  const onCallInSick = async () => {
    if (readOnly) {
      return;
    }

    const today = getAmsterdamDate();
    await command(`org/${organization}/user/${id}/leave`, {
      startDate: today,
      reason: '',
      type: 'sick',
      hours: { [today]: user?.hoursPerDay ?? org?.hoursPerDay ?? 0 }
    });
    await refreshLeaveRequests();
  };

  const onEndSickLeave = async () => {
    if (!activeSickLeave || readOnly) {
      return;
    }
    const today = getAmsterdamDate();
    await command(
      `org/${organization}/user/${id}/leave/${activeSickLeave.id}/update`,
      {
        startDate: activeSickLeave.startDate,
        endDate: today,
        reason: activeSickLeave.reason,
        type: 'sick',
        hours: getResetHours(activeSickLeave.startDate, today)
      }
    );
    await refreshLeaveRequests();
  };

  const canEditLeave = (leave: LeaveRequest) => {
    const tooOld = moment(leave.startDate, 'YYYY-MM-DD').isBefore(
      moment(),
      'day'
    );
    const leaveTypeDisabled = org?.disabledLeaveTypes.includes(leave.type);
    const isSickLeave = leave.type === 'sick';
    return (
      (!tooOld || isSickLeave) &&
      leave.state !== 'canceled' &&
      !leaveTypeDisabled
    );
  };

  return (
    <>
      <Line />
      {readOnly ? (
        <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
      ) : null}
      <Row mb={1}>
        {activeSickLeave && (
          <Alert severity="info">
            {t('sickActiveSince', {
              date: moment(activeSickLeave.startDate, 'YYYY-MM-DD').format('LL')
            })}
          </Alert>
        )}
        <Spacer />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
          style={{ gap: 8 }}
        >
          <Button
            color="primary"
            startIcon={<AddIcon />}
            aria-label={t('requestLeave')}
            onClick={onRequestLeave}
            disabled={readOnly}
          >
            {t('requestLeave')}
          </Button>
          {(canUseSickLeave || !!activeSickLeave) && (
            <Button
              color="primary"
              startIcon={
                activeSickLeave ? (
                  <CancelIcon titleAccess={t('endSickLeave')} />
                ) : (
                  <SickIcon titleAccess={t('callInSick')} />
                )
              }
              onClick={activeSickLeave ? onEndSickLeave : onCallInSick}
              disabled={readOnly}
            >
              {activeSickLeave ? t('endSickLeave') : t('callInSick')}
            </Button>
          )}
        </Box>
      </Row>
      <Table
        maxHeight="580px"
        headers={[
          t('status'),
          t('type'),
          t('start'),
          t('end'),
          t('reason'),
          ''
        ]}
        rows={
          leaveRequests.length
            ? [...leaveRequests]
                .sort((reqA, reqB) => {
                  if (reqB.state === 'canceled') {
                    return -1;
                  }
                  if (reqA.state === 'canceled') {
                    return 1;
                  }

                  return reqB.startDate.localeCompare(reqA.startDate);
                })
                .map(leave => ({
                  id: leave.id,
                  disabled: leave.state === 'canceled',
                  cells: [
                    <Box key="state" title={t(leave.state)}>
                      {getLeaveIcon(leave.state)}
                    </Box>,
                    <Box key="type" title={localizeLeaveType(t, leave.type)}>
                      {getLeaveTypeIcon(leave.type)}
                    </Box>,
                    moment(leave.startDate, 'YYYY-MM-DD').format('LL'),
                    leave.endDate
                      ? moment(leave.endDate, 'YYYY-MM-DD').format('LL')
                      : t('ongoing'),
                    <LimitedText
                      key="reason"
                      title={leave.reason}
                      variant="body2"
                    >
                      {leave.reason}
                    </LimitedText>,
                    <>
                      {!readOnly && canEditLeave(leave) && (
                        <IconButton
                          aria-label={t('editLeaveRequest')}
                          title={t('editLeaveRequest')}
                          onClick={() => onEditLeaveRequest(leave)}
                        >
                          <EditIcon color="primary" />
                        </IconButton>
                      )}
                      {!readOnly && canCancelLeave(leave) && (
                        <IconButton
                          onClick={() => onCancelLeaveRequest(leave.id)}
                        >
                          <CancelIcon color="secondary" />
                        </IconButton>
                      )}
                    </>
                  ]
                }))
            : []
        }
      />
      <ConfirmDialog
        title={confirmDialog?.title ?? ''}
        text={confirmDialog?.text ?? ''}
        dialogOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onSubmit={confirmDialog?.callback}
      />
      <RequestLeaveDialog
        title={requestLeaveDialog?.title ?? t('requestLeaveTitle')}
        hoursPerDay={user?.hoursPerDay ?? 0}
        leaveTypes={leaveTypes}
        publicHolidays={org?.publicHolidays ?? []}
        text={requestLeaveDialog?.text ?? t('requestLeaveSubtitle')}
        dialogOpen={!!requestLeaveDialog}
        onClose={() => setRequestLeaveDialog(null)}
        initialValue={requestLeaveDialog?.initialValue}
        onSubmit={requestLeaveDialog?.callback}
      />
    </>
  );
}
